// Reusable labeled input — DESIGN-SYSTEM.md §Input.
import { useId, type JSX } from "react";
import type { ApiFieldName } from "../lib/visit-report-api-fields.js";

interface Props {
  label:        string;
  value:        string;
  onChange:     (value: string) => void;
  id?:          string;
  type?:        string;
  required?:    boolean;
  placeholder?: string;
  autoComplete?: string;
  error?:       string;
  min?:         number;
  max?:         number;
  // Q9 S5 — auto-save dispatch hooks. Sections wire these to the wizard's
  // useAutoSave bus; component itself doesn't know about the save layer.
  onBlur?:      () => void;
  onValueChange?: () => void;
  // Q9 S7 — server API field name (matches the 422 error map key).
  // Rendered as `data-api-field` on the input so useFocusOnApiField can
  // find and focus it after a submit-422 navigation.
  // TASK-48 S2 — typed against the canonical schema (keyof SubmitInput +
  // template literal types for nested array / symptom_detail paths).
  apiFieldName?: ApiFieldName;
  disabled?:    boolean;
}

const BASE_CLS = "w-full rounded-lg px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 border disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed";
const OK_CLS   = "border-slate-300 focus:ring-navy-500";
const ERR_CLS  = "border-red-400 focus:ring-red-400 bg-red-50";

export function FormField({ id, type = "text", label, value, onChange, required, placeholder, autoComplete, error, min, max, onBlur, onValueChange, apiFieldName, disabled }: Props): JSX.Element {
  // useId() default: repeated labels on the same page (e.g. row-by-row
  // "Adherence" selects) would otherwise collide on a label-derived id.
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <div>
      <label htmlFor={fieldId} className="block text-sm font-medium text-slate-700 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        id={fieldId} type={type} value={value} required={required}
        autoComplete={autoComplete} placeholder={placeholder}
        min={min} max={max} disabled={disabled}
        onChange={(e) => { onChange(e.target.value); onValueChange?.(); }}
        onBlur={onBlur}
        data-api-field={apiFieldName}
        className={`${BASE_CLS} ${error ? ERR_CLS : OK_CLS}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${fieldId}-err` : undefined}
      />
      {error && <p id={`${fieldId}-err`} className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
